// frontend/src/hooks/useCommsSearch.ts
// Debounced unified comms search (people + conversations + messages) backed by
// GET /api/comms/conversations/search. Message results are membership-scoped server-side
// (IDOR-safe). Min 2 chars; results redact deleted messages.
import { useEffect, useRef, useState } from 'react'
import api from '../utils/api'

export interface CommsSearchResults {
  people: { id: number; username: string; work_title?: string; role?: string }[]
  conversations: { id: number; kind: string; title: string | null; production_id: number | null }[]
  messages: { id: number; conversation_id: number; author_id: number | null; body: string; created_at: string }[]
}

// Optional message filters. These NARROW message results server-side — they
// can never widen scope (the backend keeps the membership JOIN regardless).
// Dates are ISO 8601 strings in UTC (G-TZ).
export interface CommsSearchFilters {
  senderId?: number | null
  dateFrom?: string | null
  dateTo?: string | null
  hasAttachment?: boolean
}

const EMPTY: CommsSearchResults = { people: [], conversations: [], messages: [] }
const DEBOUNCE_MS = 250
const MIN_CHARS = 2

export function useCommsSearch(
  query: string,
  type: 'all' | 'people' | 'conversations' | 'messages' = 'all',
  filters: CommsSearchFilters = {},
) {
  const [results, setResults] = useState<CommsSearchResults>(EMPTY)
  const [loading, setLoading] = useState(false)
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Serialize filters to a primitive so the effect re-runs only on real change
  // (not on a new object identity each render).
  const { senderId, dateFrom, dateTo, hasAttachment } = filters
  const filterKey = `${senderId ?? ''}|${dateFrom ?? ''}|${dateTo ?? ''}|${hasAttachment ? '1' : ''}`

  useEffect(() => {
    const q = query.trim()
    if (q.length < MIN_CHARS) { setResults(EMPTY); setLoading(false); return }
    setLoading(true)
    if (timer.current) clearTimeout(timer.current)
    timer.current = setTimeout(async () => {
      try {
        const params = new URLSearchParams({ q, type })
        if (senderId != null) params.set('senderId', String(senderId))
        if (dateFrom) params.set('dateFrom', dateFrom)
        if (dateTo) params.set('dateTo', dateTo)
        if (hasAttachment) params.set('hasAttachment', '1')
        const { data } = await api.get<CommsSearchResults>(`/comms/conversations/search?${params.toString()}`)
        setResults({ people: data?.people ?? [], conversations: data?.conversations ?? [], messages: data?.messages ?? [] })
      } catch {
        setResults(EMPTY)
      } finally {
        setLoading(false)
      }
    }, DEBOUNCE_MS)
    return () => { if (timer.current) clearTimeout(timer.current) }
  }, [query, type, filterKey, senderId, dateFrom, dateTo, hasAttachment])

  return { results, loading }
}
